/**
 * Soft / hard AI usage limits, reserve percentage, and warning thresholds.
 *
 * Pure validation and evaluation. Persistence is a separate owner RPC.
 * Explicit offering pins are never weakened here — limits only advise.
 */

export const USAGE_RESERVE_MIN_PERCENT = 0;
export const USAGE_RESERVE_MAX_PERCENT = 90;

/** Default warn thresholds the settings surface offers. */
export const DEFAULT_WARN_THRESHOLDS = [50, 75, 90, 100] as const;

export type ExhaustionBehavior =
  | 'advise_only'
  | 'auto_route'
  | 'pause_lane'
  | 'require_approval';

export interface AiUsagePolicy {
  /** Soft ceiling as percent of allowance (warn / advise). Null = unset. */
  readonly softLimitPercent: number | null;
  /** Hard ceiling as percent of allowance (block lane / require action). Null = unset. */
  readonly hardLimitPercent: number | null;
  /** Share of allowance kept for high-priority work. */
  readonly reservePercent: number | null;
  /** Warn when used percent crosses these marks (sorted unique 1–100). */
  readonly warnThresholds: readonly number[];
  readonly exhaustionBehavior: ExhaustionBehavior;
  /**
   * When true, automatic fallback must never replace an explicit pin.
   * Default true — Phase 1 preserves existing pin semantics.
   */
  readonly neverSubstitutePinned: boolean;
}

export const DEFAULT_AI_USAGE_POLICY: AiUsagePolicy = {
  softLimitPercent: null,
  hardLimitPercent: null,
  reservePercent: null,
  warnThresholds: [...DEFAULT_WARN_THRESHOLDS],
  exhaustionBehavior: 'advise_only',
  neverSubstitutePinned: true,
};

export interface AiUsagePolicyInput {
  readonly softLimitPercent: string;
  readonly hardLimitPercent: string;
  readonly reservePercent: string;
  readonly warnThresholds: string;
  readonly exhaustionBehavior: string;
  readonly neverSubstitutePinned: boolean;
}

export type AiUsagePolicyCheck =
  | { readonly ok: true; readonly value: AiUsagePolicy }
  | { readonly ok: false; readonly message: string };

export type UsageLimitBand =
  | 'unknown'
  | 'ok'
  | 'warn'
  | 'soft_exceeded'
  | 'reserve_breached'
  | 'hard_exceeded'
  | 'exhausted';

export interface UsageLimitEvaluation {
  readonly band: UsageLimitBand;
  /** Used percent when it can be derived honestly; otherwise null. */
  readonly usedPercent: number | null;
  readonly crossedWarnAt: number | null;
  readonly withinReserve: boolean | null;
}

function parseOptionalPercent(text: string, label: string):
  | { ok: true; value: number | null }
  | { ok: false; message: string } {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: null };
  if (!/^[0-9]{1,3}$/.test(trimmed)) {
    return { ok: false, message: `${label} must be a whole number of percent between 0 and 100.` };
  }
  const value = Number(trimmed);
  if (value < 0 || value > 100) {
    return { ok: false, message: `${label} must be between 0 and 100 percent.` };
  }
  return { ok: true, value };
}

function parseReserve(text: string):
  | { ok: true; value: number | null }
  | { ok: false; message: string } {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: null };
  if (!/^[0-9]{1,2}$/.test(trimmed)) {
    return {
      ok: false,
      message: `The reserve must be a whole number of percent between ${USAGE_RESERVE_MIN_PERCENT} and ${USAGE_RESERVE_MAX_PERCENT}.`,
    };
  }
  const value = Number(trimmed);
  if (value < USAGE_RESERVE_MIN_PERCENT || value > USAGE_RESERVE_MAX_PERCENT) {
    return {
      ok: false,
      message: `The reserve must be between ${USAGE_RESERVE_MIN_PERCENT} and ${USAGE_RESERVE_MAX_PERCENT} percent.`,
    };
  }
  return { ok: true, value };
}

function parseWarnThresholds(text: string):
  | { ok: true; value: number[] }
  | { ok: false; message: string } {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: [...DEFAULT_WARN_THRESHOLDS] };
  const parts = trimmed.split(/[,\s]+/).filter((part) => part.length > 0);
  const values: number[] = [];
  for (const part of parts) {
    if (!/^[0-9]{1,3}$/.test(part)) {
      return { ok: false, message: 'Warn thresholds must be whole numbers of percent between 1 and 100.' };
    }
    const value = Number(part);
    if (value < 1 || value > 100) {
      return { ok: false, message: 'Warn thresholds must be between 1 and 100 percent.' };
    }
    if (!values.includes(value)) values.push(value);
  }
  values.sort((a, b) => a - b);
  return { ok: true, value: values };
}

const EXHAUSTION: readonly ExhaustionBehavior[] = [
  'advise_only',
  'auto_route',
  'pause_lane',
  'require_approval',
];

/**
 * Validate owner-typed policy fields. Empty soft/hard/reserve keeps them unset.
 * Soft must be below hard when both are set. Pins stay protected by default.
 */
export function checkAiUsagePolicy(input: AiUsagePolicyInput): AiUsagePolicyCheck {
  const soft = parseOptionalPercent(input.softLimitPercent, 'The soft limit');
  if (!soft.ok) return soft;
  const hard = parseOptionalPercent(input.hardLimitPercent, 'The hard limit');
  if (!hard.ok) return hard;
  const reserve = parseReserve(input.reservePercent);
  if (!reserve.ok) return reserve;
  const warns = parseWarnThresholds(input.warnThresholds);
  if (!warns.ok) return warns;

  const behavior = input.exhaustionBehavior.trim() as ExhaustionBehavior;
  if (!EXHAUSTION.includes(behavior)) {
    return { ok: false, message: 'Choose how Brunel should behave when a model is exhausted.' };
  }

  if (soft.value !== null && hard.value !== null && soft.value > hard.value) {
    return { ok: false, message: 'The soft limit must be at or below the hard limit.' };
  }

  return {
    ok: true,
    value: {
      softLimitPercent: soft.value,
      hardLimitPercent: hard.value,
      reservePercent: reserve.value,
      warnThresholds: warns.value,
      exhaustionBehavior: behavior,
      neverSubstitutePinned: input.neverSubstitutePinned,
    },
  };
}

/**
 * Used percent from used/remaining/limit without inventing a denominator.
 * Prefer used when present; else limit−remaining when both exist; else null.
 */
export function usedPercentOf(
  used: number | null,
  remaining: number | null,
  limit: number | null,
): number | null {
  const clamp = (value: number): number => Math.max(0, Math.min(100, Math.round(value * 1e6) / 1e6));
  if (used !== null && Number.isFinite(used)) {
    if (limit !== null && Number.isFinite(limit) && limit > 0) {
      return clamp((used / limit) * 100);
    }
    // Percent unit often stores used directly as 0–100.
    if (used >= 0 && used <= 100 && (limit === null || limit === 100)) return clamp(used);
  }
  if (remaining !== null && limit !== null && Number.isFinite(remaining) && Number.isFinite(limit) && limit > 0) {
    return clamp(((limit - remaining) / limit) * 100);
  }
  if (remaining !== null && Number.isFinite(remaining) && remaining >= 0 && remaining <= 100 && limit === null) {
    return clamp(100 - remaining);
  }
  return null;
}

/** Evaluate a pool reading against the owner's policy. Honest unknown when no %. */
export function evaluateUsageLimits(
  usedPercent: number | null,
  policy: AiUsagePolicy,
  exhausted: boolean,
): UsageLimitEvaluation {
  if (exhausted) {
    return {
      band: 'exhausted',
      usedPercent,
      crossedWarnAt: usedPercent === null ? null : highestCrossed(usedPercent, policy.warnThresholds),
      withinReserve: policy.reservePercent === null || usedPercent === null
        ? null
        : usedPercent <= 100 - policy.reservePercent,
    };
  }
  if (usedPercent === null) {
    return { band: 'unknown', usedPercent: null, crossedWarnAt: null, withinReserve: null };
  }

  const withinReserve = policy.reservePercent === null
    ? null
    : usedPercent <= 100 - policy.reservePercent;

  if (policy.hardLimitPercent !== null && usedPercent >= policy.hardLimitPercent) {
    return {
      band: 'hard_exceeded',
      usedPercent,
      crossedWarnAt: highestCrossed(usedPercent, policy.warnThresholds),
      withinReserve,
    };
  }
  if (policy.reservePercent !== null && usedPercent > 100 - policy.reservePercent) {
    return {
      band: 'reserve_breached',
      usedPercent,
      crossedWarnAt: highestCrossed(usedPercent, policy.warnThresholds),
      withinReserve: false,
    };
  }
  if (policy.softLimitPercent !== null && usedPercent >= policy.softLimitPercent) {
    return {
      band: 'soft_exceeded',
      usedPercent,
      crossedWarnAt: highestCrossed(usedPercent, policy.warnThresholds),
      withinReserve,
    };
  }
  const crossed = highestCrossed(usedPercent, policy.warnThresholds);
  if (crossed !== null) {
    return { band: 'warn', usedPercent, crossedWarnAt: crossed, withinReserve };
  }
  return { band: 'ok', usedPercent, crossedWarnAt: null, withinReserve };
}

function highestCrossed(usedPercent: number, thresholds: readonly number[]): number | null {
  let hit: number | null = null;
  for (const mark of thresholds) {
    if (usedPercent >= mark) hit = mark;
  }
  return hit;
}
